// The one way the app talks to /api: JSON both ways, our cookies, and a
// failure that carries the server's own plain words (ApiError). A mutation
// always sends a JSON body, since the Worker refuses one that is not JSON.
// A dropped connection is a failure too, with its own words, so a screen
// never shows a raw status.
import type { ApiError } from '@shared/api';

export class ApiFailure extends Error {
  readonly status: number;
  readonly body: ApiError;
  constructor(status: number, body: ApiError) {
    super(body.message);
    this.name = 'ApiFailure';
    this.status = status;
    this.body = body;
  }
}

export interface ApiOptions {
  /** GET without a body, POST with one, unless given. */
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
}

export const OFFLINE: ApiError = { error: 'offline', message: 'You’re offline. Try again when you have a connection.' };
export const UNREACHABLE: ApiError = { error: 'unreachable', message: 'We couldn’t reach Honey-Do. Try again in a moment.' };
export const BROKEN: ApiError = { error: 'unknown', message: 'Something went wrong on our side. Try again in a moment.' };

const isApiError = (v: unknown): v is ApiError =>
  !!v && typeof v === 'object' && typeof (v as ApiError).error === 'string' && typeof (v as ApiError).message === 'string';

export async function api<T>(path: string, { method, body, signal }: ApiOptions = {}): Promise<T> {
  const verb = method ?? (body === undefined ? 'GET' : 'POST');
  const headers: Record<string, string> = { Accept: 'application/json' };
  const init: RequestInit = { method: verb, credentials: 'same-origin', headers, signal };
  if (verb !== 'GET') {
    headers['Content-Type'] = 'application/json';
    init.body = JSON.stringify(body ?? {});
  }
  let res: Response;
  try {
    res = await fetch(path, init);
  } catch (err) {
    if (signal?.aborted) throw err;
    throw new ApiFailure(0, typeof navigator !== 'undefined' && navigator.onLine === false ? OFFLINE : UNREACHABLE);
  }
  const data: unknown = await res.json().catch(() => null);
  if (!res.ok) throw new ApiFailure(res.status, isApiError(data) ? data : BROKEN);
  return data as T;
}

/** The words to show for anything a call threw. */
export function failureMessage(err: unknown): string {
  return err instanceof ApiFailure ? err.body.message : BROKEN.message;
}
